import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { User, Mail, Phone, Building2, Users, Trophy, TrendingUp, Clock, Target, Award, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const ROLE_LABELS = {
    admin_full: 'Admin',
    admin: 'Admin',
    vp: 'VP',
    apporteur: 'Apporteur'
};

const ROLE_COLORS = {
    admin_full: 'bg-purple-50 text-purple-700 border-purple-200',
    admin: 'bg-orange-50 text-orange-700 border-orange-200',
    vp: 'bg-blue-50 text-blue-700 border-blue-200',
    apporteur: 'bg-green-50 text-green-700 border-green-200'
};

export default function UserProfileModal({ userId, isOpen, onClose }) {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && userId) {
            setLoading(true);
            api.get(`/users/${userId}/profile`)
                .then(res => setProfile(res.data))
                .catch(err => {
                    const msg = err.response?.data?.detail || 'Erreur lors du chargement du profil';
                    toast.error(msg);
                })
                .finally(() => setLoading(false));
        }
    }, [isOpen, userId]);

    if (!isOpen) return null;

    const initials = profile 
        ? `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase()
        : '';

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="user-profile-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <User className="w-4 h-4" />
                        Fiche Contact
                    </DialogTitle>
                </DialogHeader>

                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                )}

                {profile && !loading && (
                    <div className="space-y-5">
                        {/* Identity */}
                        <div className="flex items-center gap-4">
                            <Avatar className="w-16 h-16 ring-2 ring-offset-2 ring-slate-200">
                                <AvatarFallback className="bg-gradient-to-br from-slate-700 to-slate-900 text-white text-lg font-bold">
                                    {initials}
                                </AvatarFallback>
                            </Avatar>
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">
                                    {profile.first_name} {profile.last_name}
                                </h2>
                                <Badge variant="outline" className={ROLE_COLORS[profile.role] || ''}>
                                    {ROLE_LABELS[profile.role] || profile.role}
                                </Badge>
                            </div>
                        </div>

                        {/* Contact Info */}
                        <Card className="border-slate-200">
                            <CardContent className="py-4 space-y-3">
                                {profile.email && (
                                    <a href={`mailto:${profile.email}`} className="flex items-center gap-3 text-sm text-slate-700 hover:text-blue-600 transition-colors" data-testid="profile-email">
                                        <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                                            <Mail className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <span>{profile.email}</span>
                                    </a>
                                )}
                                {profile.phone && (
                                    <a href={`tel:${profile.phone}`} className="flex items-center gap-3 text-sm text-slate-700 hover:text-emerald-600 transition-colors" data-testid="profile-phone">
                                        <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                                            <Phone className="w-4 h-4 text-emerald-600" />
                                        </div>
                                        <span>{profile.phone}</span>
                                    </a>
                                )}
                                {!profile.email && !profile.phone && (
                                    <p className="text-sm text-slate-400 text-center py-2">Aucune coordonnée renseignée</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* VP Referent */}
                        {profile.vp_referent && (
                            <Card className="bg-purple-50/50 border-purple-200">
                                <CardContent className="py-3 px-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Users className="w-4 h-4 text-purple-600" />
                                        <span className="text-xs font-medium text-purple-600">Chef d'équipe</span>
                                    </div>
                                    <p className="font-medium text-slate-900">{profile.vp_referent.name}</p>
                                    {profile.vp_referent.phone && (
                                        <a href={`tel:${profile.vp_referent.phone}`} className="text-xs text-purple-600 hover:underline">{profile.vp_referent.phone}</a>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Teams */}
                        {profile.teams?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {profile.teams.map((t, i) => (
                                    <Badge key={i} variant="outline" className="gap-1.5 text-xs">
                                        <Building2 className="w-3 h-3" />
                                        {t.name} {t.role_label && `(${t.role_label})`}
                                    </Badge>
                                ))}
                            </div>
                        )}

                        {/* Statistics */}
                        {profile.statistics && (
                            <div className="grid grid-cols-2 gap-2">
                                <Card className="bg-emerald-50/50 border-emerald-200">
                                    <CardContent className="py-3 px-3 text-center">
                                        <Trophy className="w-4 h-4 text-emerald-600 mx-auto mb-1" />
                                        <p className="text-xl font-bold text-emerald-700">{profile.statistics.won_prospects}</p>
                                        <p className="text-[10px] text-emerald-600">Gagnés</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-blue-50/50 border-blue-200">
                                    <CardContent className="py-3 px-3 text-center">
                                        <Target className="w-4 h-4 text-blue-600 mx-auto mb-1" />
                                        <p className="text-xl font-bold text-blue-700">{profile.statistics.success_rate}%</p>
                                        <p className="text-[10px] text-blue-600">Taux réussite</p>
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* Company info */}
                        {profile.company_info && (
                            <Card>
                                <CardContent className="py-3 px-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Building2 className="w-4 h-4 text-slate-500" />
                                        <span className="text-xs font-medium text-slate-500">Société</span>
                                    </div>
                                    <p className="font-medium text-sm">{profile.company_info.company_name}</p>
                                    {profile.company_info.siret && <p className="text-xs text-slate-500">SIRET: {profile.company_info.siret}</p>}
                                </CardContent>
                            </Card>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
